/**
 * OUW-67 / PAY-4: handoff to ole-web-payment.
 *
 * The Client Portal never embeds payment in an iframe or modal. The live CTA
 * opens the full `payment_url` from GET …/applications/:quoteId
 * (`{PAYMENT_WEB_BASE_URL}/link?subscription=<hash>`) in a new tab so the
 * status page stays put (OUW-331). If the popup is blocked, it falls back to
 * this tab. Only https is accepted (http solo contra localhost, el arnés
 * local) so a bad payload cannot become a `javascript:` navigation nor send
 * the customer to an unencrypted checkout.
 *
 * The new tab is opened as `about:blank` and then pointed at the payment URL.
 * `window.open(crossOrigin, '_blank', 'noopener')` returns null even when the
 * tab opened (HTML spec), so treating that null as "popup blocked" navigated
 * this tab as well — two checkouts. Same constraint as `openOfferDocument`.
 */

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

/** The URL to navigate to, or null when it is not a safe payment URL. */
function safePaymentUrl(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'https:') return trimmed;
    if (parsed.protocol === 'http:' && LOCAL_HOSTS.has(parsed.hostname)) return trimmed;
    return null;
  } catch {
    return null;
  }
}

export function isSafePaymentUrl(url: unknown): url is string {
  return safePaymentUrl(url) !== null;
}

/**
 * Marks the payment as started for `quoteId` and opens the portal. The mark
 * goes first so the status page can show the confirming state even when the
 * new tab is blocked and this tab navigates away.
 */
export function handoffToPaymentUrl(
  url: unknown,
  quoteId: string,
  navigate: (href: string) => void = openPaymentTab,
): boolean {
  const href = safePaymentUrl(url);
  if (!href) return false;
  markPaymentStarted(quoteId);
  navigate(href);
  return true;
}

function openPaymentTab(href: string): void {
  // Empty target: same-origin about:blank, so a real Window comes back iff
  // the popup was allowed. Null the opener before leaving our origin so the
  // payment site cannot reach this page (noopener equivalent).
  const opened = window.open('', '_blank');
  if (!opened) {
    window.location.assign(href);
    return;
  }
  opened.opener = null;
  opened.location.href = href;
}

/**
 * La marca de «este cliente salió a pagar» (OUW-67).
 *
 * ole-web-payment no vuelve solo al portal, y el aviso de primer pago llega
 * por otro camino (pasarela → pagos → pólizas → api-client): de segundos a
 * minutos con tarjeta, días con transferencia. Sin la marca, al volver el
 * cliente ve otra vez «Pagar» como si no hubiera pasado nada, y el link de
 * pagos puede cobrarle de nuevo (OUW-236).
 *
 * Va en `localStorage`, no en `sessionStorage` como la de la firma (OUW-186):
 * del pago se suele volver desde el correo o en otra pestaña. Es por
 * navegador: en otro dispositivo no hay marca. Se limpia cuando llega el aviso
 * o la solicitud se mueve, y vence sola.
 *
 * Una entrada por solicitud (pagar una no pisa la espera de otra), guardada
 * por un hash del quoteId: el identificador que abre el tablero no queda
 * legible en el almacenamiento de un equipo compartido.
 */
const PAYMENT_MARKS = 'ole.portal.paymentStarted';

/** Después de esto se deja de esperar el aviso y vuelve «Pagar». */
export const PAYMENT_CONFIRM_WAIT_MS = 24 * 60 * 60 * 1000;

/** hash del quoteId → cuándo salió a pagar. */
type PaymentMarks = Record<string, number>;

export function markPaymentStarted(quoteId: string, now = Date.now()): void {
  try {
    const marks = liveMarks(readPaymentMarks(), now);
    marks[markKey(quoteId)] = now;
    writePaymentMarks(marks);
  } catch {
    // Navegación privada o almacenamiento bloqueado: se pierde el aviso de
    // espera, no el pago. No vale la pena frenar la salida a pagar por esto.
  }
}

/** Borra solo la marca de esta solicitud: otra pestaña puede estar pagando otra. */
export function clearPaymentMark(quoteId: string): void {
  try {
    const marks = readPaymentMarks();
    const key = markKey(quoteId);
    if (!(key in marks)) return;
    delete marks[key];
    writePaymentMarks(marks);
  } catch {
    /* idem */
  }
}

/** Cuándo salió a pagar ESTA solicitud, o null si no hay marca vigente. */
export function paymentStartedAt(quoteId: string, now = Date.now()): number | null {
  try {
    const at = readPaymentMarks()[markKey(quoteId)];
    return typeof at === 'number' && now - at < PAYMENT_CONFIRM_WAIT_MS ? at : null;
  } catch {
    return null;
  }
}

/** Las marcas vencidas se descartan al escribir, para que la clave no crezca. */
function liveMarks(marks: PaymentMarks, now: number): PaymentMarks {
  const live: PaymentMarks = {};
  Object.entries(marks).forEach(([key, at]) => {
    if (now - at < PAYMENT_CONFIRM_WAIT_MS) live[key] = at;
  });
  return live;
}

function readPaymentMarks(): PaymentMarks {
  const raw = window.localStorage.getItem(PAYMENT_MARKS);
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Un valor ilegible (otra versión, una extensión) cuenta como vacío: si
    // tirara, `markPaymentStarted` no podría volver a escribir nunca.
    return {};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const marks: PaymentMarks = {};
  Object.entries(parsed as Record<string, unknown>).forEach(([key, at]) => {
    if (typeof at === 'number') marks[key] = at;
  });
  return marks;
}

function writePaymentMarks(marks: PaymentMarks): void {
  if (Object.keys(marks).length === 0) {
    window.localStorage.removeItem(PAYMENT_MARKS);
    return;
  }
  window.localStorage.setItem(PAYMENT_MARKS, JSON.stringify(marks));
}

/**
 * FNV-1a de 32 bits. No es criptográfico: alcanza para comparar sin dejar el
 * quoteId en claro, y es síncrono (la marca se escribe justo antes de navegar).
 */
function markKey(quoteId: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < quoteId.length; i += 1) {
    hash ^= quoteId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}
